import ls from 'localstorage-slim';

import { isAllowedEnvironment } from './is-allowed-environment';

export const EXPERIMENT_OVERRIDES_STORAGE_KEY = '__DATABASES_EXPERIMENT_OVERRIDES';

const storageInstance = ls;

type ExperimentOverridesMap = Record<string, Record<string, any>>;

export const mergeExperimentOverrides = (
  ...overrideMaps: ExperimentOverridesMap[]
): ExperimentOverridesMap => {
  return overrideMaps.reduce<ExperimentOverridesMap>((mergedOverrides, overrides) => {
    Object.entries(overrides).forEach(([experimentName, params]) => {
      mergedOverrides[experimentName] = {
        ...mergedOverrides[experimentName],
        ...params,
      };
    });

    return mergedOverrides;
  }, {});
};

/**
 * Determine whether there are any persisted experiment overrides.
 */
export const hasExperimentOverrides = (): boolean =>
  storageInstance.get(EXPERIMENT_OVERRIDES_STORAGE_KEY) !== null;

/**
 * Get persisted experiment overrides from localStorage.
 * Returns an empty object in production to prevent overrides from leaking.
 */
export const getPersistedExperimentOverrides = (): ExperimentOverridesMap => {
  if (!isAllowedEnvironment()) {
    return {};
  }

  if (!hasExperimentOverrides()) {
    return {};
  }

  return (
    (storageInstance.get(EXPERIMENT_OVERRIDES_STORAGE_KEY) as ExperimentOverridesMap) ??
    {}
  );
};

/**
 * Set a single experiment param override and persist to localStorage.
 */
export const setPersistedExperimentOverride = (
  experimentName: string,
  paramName: string,
  value: any,
): void => {
  const overrides = getPersistedExperimentOverrides();

  overrides[experimentName] = {
    ...overrides[experimentName],
    [paramName]: value,
  };

  storageInstance.set(EXPERIMENT_OVERRIDES_STORAGE_KEY, overrides);
};

/**
 * Remove all overrides for a specific experiment and persist to localStorage.
 */
export const removePersistedExperimentOverride = (experimentName: string): void => {
  const overrides = getPersistedExperimentOverrides();
  delete overrides[experimentName];

  if (Object.keys(overrides).length === 0) {
    storageInstance.remove(EXPERIMENT_OVERRIDES_STORAGE_KEY);
  } else {
    storageInstance.set(EXPERIMENT_OVERRIDES_STORAGE_KEY, overrides);
  }
};

/**
 * Remove a single parameter override for a specific experiment.
 */
export const removePersistedExperimentParamOverride = (
  experimentName: string,
  paramName: string,
): void => {
  const overrides = getPersistedExperimentOverrides();
  const experimentOverrides = overrides[experimentName];

  if (!experimentOverrides) {
    return;
  }

  delete experimentOverrides[paramName];

  if (Object.keys(experimentOverrides).length === 0) {
    delete overrides[experimentName];
  }

  if (Object.keys(overrides).length === 0) {
    storageInstance.remove(EXPERIMENT_OVERRIDES_STORAGE_KEY);
  } else {
    storageInstance.set(EXPERIMENT_OVERRIDES_STORAGE_KEY, overrides);
  }
};

/**
 * Clear all persisted experiment overrides.
 */
export const clearPersistedExperimentOverrides = (): void => {
  storageInstance.remove(EXPERIMENT_OVERRIDES_STORAGE_KEY);
};
